import { useState, Dispatch, SetStateAction, ChangeEvent } from "react";
import styled from "styled-components/macro";
import SidePanel, { SidePanelTrigger } from "./SidePanel";
import {
  BoxHeader,
  BoxContent,
  BoxNavRight,
  BoxNavSearch,
  BoxNavLeft,
  BoxNavButton,
  BoxFooter,
} from "./Box";
import Table, { Column } from "./Table";
import Highlight from "./Highlight";
import ContactsWell from "./ContactsWell";
import Checkbox from "./Checkbox";
import { Segments, Segment } from "./SegmentedControl";
import Button from "./Button";
import { Close } from "./Icons";
import Stack from "./Stack";
import useExitWarning from "../hooks/use-prevent-exit";

type Value = {
  contactIds: string[];
  groupIds: string[];
  keywordIds: string[];
};

type SharedProps = {
  value?: Value;
  onChange?: Dispatch<SetStateAction<Value>>;
  onSelect?: (contactId: string) => void;
  allowGroups?: boolean;
  limit?: number;
};

function ContactSelector({
  trigger,
  ...props
}: { trigger: SidePanelTrigger } & SharedProps) {
  return (
    <SidePanel
      trigger={trigger}
      content={({ hide }) => <ContactSelectorContent {...props} hide={hide} />}
    />
  );
}

function ContactSelectorContent({
  hide,
  value: initialValue = { contactIds: [], keywordIds: [], groupIds: [] },
  onChange: updateParent = () => {},
  onSelect,
  allowGroups = false,
  limit = onSelect ? 1 : Infinity,
}: { hide: () => void } & SharedProps) {
  const [firstValue] = useState(initialValue);
  const [value, onChange] = useState(initialValue);
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("contacts");
  useExitWarning(firstValue !== value);

  return (
    <>
      <BoxHeader>
        <BoxNavLeft>
          <BoxNavSearch
            placeholder={
              mode === "contacts" ? "Search Contacts" : "Search Groups"
            }
            value={query}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setQuery(e.target.value)
            }
            autoFocus
          />
        </BoxNavLeft>
        <BoxNavRight>
          <BoxNavButton onClick={hide}>
            <Close />
          </BoxNavButton>
        </BoxNavRight>
      </BoxHeader>
      <FlexedBoxContent>
        <Stack>
          {limit !== 1 && (
            <ContactsWell
              value={value}
              onChange={onChange}
              canAdd={false}
              canRemove={true}
            />
          )}

          <Segments value={mode} onChange={setMode}>
            <Segment value="contacts">Contacts</Segment>
            <Segment value="groups">Groups</Segment>
          </Segments>

          {mode === "contacts" ? (
            <Table
              key="contacts"
              endpoint={({ skipioApi }) => skipioApi.addressBook.get}
              params={{ query, sort: "first_name", order: "asc" }}
              isSelected={(item) => value.contactIds.includes(item.id)}
              onRowClick={({ rowData: contact }) => {
                if (limit === 1 && onSelect) {
                  onSelect(contact);
                  hide();
                  return;
                }
                onChange((value) => {
                  let contactIds = [...value.contactIds];
                  if (!contactIds.includes(contact.id)) {
                    contactIds.push(contact.id);
                  } else {
                    contactIds = contactIds.filter((id) => id !== contact.id);
                  }

                  return {
                    ...value,
                    contactIds: contactIds.slice(0, limit),
                  };
                });
              }}
            >
              {limit !== 1 && (
                <Column
                  width={"32px"}
                  label=""
                  dataKey="checked"
                  cellRenderer={({ rowData: contact }) => {
                    const active = value.contactIds.includes(contact.id);
                    return (
                      <Checkbox
                        style={{ transform: "translate(-4px, -10px)" }}
                        value={active}
                        onChange={() => {
                          /* noop. Handled by onRowClick */
                        }}
                      />
                    );
                  }}
                />
              )}
              <Column
                width={1}
                label="First Name"
                dataKey="firstName"
                cellRenderer={({ rowData: contact }) => (
                  <span data-ignore="true">
                    <Highlight query={query}>{contact.firstName}</Highlight>
                  </span>
                )}
              />
              <Column
                width={1}
                label="Last Name"
                dataKey="lastName"
                cellRenderer={({ rowData: contact }) => (
                  <span data-ignore="true">
                    <Highlight query={query}>{contact.lastName}</Highlight>
                  </span>
                )}
              />
              <Column
                width={1}
                label="Phone Number"
                dataKey="phoneMobile"
                cellRenderer={({ rowData: contact }) => (
                  <span data-ignore="true">
                    <Highlight query={query}>
                      {contact.phoneMobile.replace(
                        /^\+\d(\d{3})(\d{3})(\d{4})/,
                        "($1) $2-$3"
                      )}
                    </Highlight>
                  </span>
                )}
              />
            </Table>
          ) : mode === "groups" ? (
            <Table
              key="groups"
              endpoint={({ skipioApi }) => skipioApi.groups.get}
              params={{ query }}
              isSelected={(item) => value.groupIds.includes(item.id)}
              onRowClick={({ rowData: group }) => {
                const active = allowGroups
                  ? value.groupIds.includes(group.id)
                  : group.contactIds.every((id: string) =>
                      value.contactIds.includes(id)
                    );

                if (allowGroups) {
                  onChange((value) => {
                    let groupIds = [...value.groupIds];

                    if (!active) {
                      groupIds.push(group.id);
                    } else {
                      groupIds = groupIds.filter((id) => id !== group.id);
                    }

                    return {
                      ...value,
                      groupIds,
                    };
                  });
                } else {
                  // if groups aren't allowed then add/remove contacts individually
                  onChange((value) => {
                    let contactIds = [...value.contactIds];

                    for (let contactId of group.contactIds) {
                      if (!active) {
                        contactIds.push(contactId);
                      } else {
                        contactIds = contactIds.filter(
                          (id) => id !== contactId
                        );
                      }
                    }

                    contactIds = Array.from(new Set(contactIds));

                    return {
                      ...value,
                      contactIds: contactIds.slice(0, limit),
                    };
                  });
                }
              }}
            >
              <Column
                width={"32px"}
                label=""
                dataKey="checked"
                cellRenderer={({ rowData: group }) => {
                  const active = allowGroups
                    ? value.groupIds.includes(group.id)
                    : group.contactIds.length > 0 &&
                      group.contactIds.every((id: string) =>
                        value.contactIds.includes(id)
                      );

                  const indeterminate = allowGroups
                    ? undefined
                    : !active &&
                      group.contactIds.length > 0 &&
                      group.contactIds.some((id: string) =>
                        value.contactIds.includes(id)
                      );
                  return (
                    <Checkbox
                      indeterminate={indeterminate}
                      style={{ transform: "translate(-4px, -10px)" }}
                      value={active}
                      onChange={() => {
                        /* noop. Handled by onRowClick */
                      }}
                    />
                  );
                }}
              />
              <Column
                width={1}
                label="Group Name"
                dataKey="name"
                cellRenderer={({ rowData: group }) => (
                  <span data-ignore="true">
                    <Highlight query={query}>{group.name}</Highlight>
                  </span>
                )}
              />
              <Column
                width={"160px"}
                label="Contacts Count"
                dataKey="contactIds"
                cellRenderer={({ rowData: group }) => group.contactIds.length}
              />
            </Table>
          ) : (
            <Table
              key="keywords"
              endpoint={({ skipioApi }) => skipioApi.keywords.get}
              params={{ query }}
              isSelected={(item) => value.groupIds.includes(item.id)}
              onRowClick={({ rowData: keyword }) => {
                onChange((value) => {
                  let keywordIds = [...value.keywordIds];

                  if (!value.keywordIds.includes(keyword.id)) {
                    keywordIds.push(keyword.id);
                  } else {
                    keywordIds = keywordIds.filter((id) => id !== keyword.id);
                  }

                  return {
                    ...value,
                    keywordIds,
                    contactIds: [...value.contactIds],
                  };
                });
              }}
            >
              <Column
                width={"32px"}
                label=""
                dataKey="checked"
                cellRenderer={({ rowData: keyword }) => {
                  const active = value.keywordIds.includes(keyword.id);
                  return (
                    <Checkbox
                      style={{ transform: "translate(-4px, -10px)" }}
                      value={active}
                      onChange={() => {
                        /* noop. Handled by onRowClick */
                      }}
                    />
                  );
                }}
              />
              <Column
                width={1}
                label="Keyword group"
                dataKey="keyword"
                cellRenderer={({ rowData: keyword }) => (
                  <span data-ignore="true">
                    <Highlight query={query}>{keyword.keyword}</Highlight>
                  </span>
                )}
              />
              <Column
                width={"160px"}
                label="Subscribers Count"
                dataKey="activeSubscriberCount"
                cellRenderer={({ rowData: keyword }) =>
                  keyword.activeSubscriberCount
                }
              />
            </Table>
          )}
        </Stack>
      </FlexedBoxContent>
      <BoxFooter>
        <BoxNavLeft>
          <Button onClick={hide} variant="link">
            Cancel
          </Button>
        </BoxNavLeft>
        <BoxNavRight>
          <Button
            onClick={() => {
              hide();
              updateParent(value);
            }}
            disabled={firstValue === value}
          >
            Confirm
          </Button>
        </BoxNavRight>
      </BoxFooter>
    </>
  );
}

const FlexedBoxContent = styled(BoxContent)`
  display: flex;
  flex-direction: column;
`;

export default ContactSelector;
